import {useEffect, useMemo, useState} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {alpha} from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {useTranslation} from 'react-i18next';
import type {Node} from 'reactflow';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import type {FlowPreview} from 'crqlar-react-common/dist/models/automation/flowPreview.model';
import {getNodeColor, getNodeDisplay} from '../config/nodeCategories';
import BoldNumbers from './BoldNumbers';
import {previewCount} from './flowPreviewModel';
import {matchesSearch, stepLists} from './previewLists';
import {countMeaning, previewScope} from './previewText';
import StepPeopleTable, {PeopleColumns} from './StepPeopleTable';

interface StepPreviewDialogProps {
    /** The step whose number was clicked; null keeps the dialog closed. */
    nodeId: string | null;
    onClose: () => void;
    preview?: FlowPreview;
    nodes: Node[];
}

const SEARCH_FROM = 10;

const StepPreviewDialog = ({nodeId, onClose, preview, nodes}: StepPreviewDialogProps) => {
    const {t, i18n} = useTranslation();
    const language = i18n?.resolvedLanguage || i18n?.language;
    const numbers = useMemo(() => new Intl.NumberFormat(language), [language]);
    const collator = useMemo(() => new Intl.Collator(language, {sensitivity: 'base'}), [language]);

    const [search, setSearch] = useState('');
    useEffect(() => setSearch(''), [nodeId]);

    const node = nodeId ? nodes.find(entry => entry.id === nodeId) : undefined;
    const type = node?.data.type as NodeType | undefined;
    const label = node ? node.data.label || t(getNodeDisplay(node.data.type).label) : '';
    const color = type ? getNodeColor(type) : '#9ca3af';

    const count = preview && node && type ? previewCount(node.id, type, preview) : null;
    const meaning = count && preview && node && type ? countMeaning(node.id, type, count, preview, t) : null;
    const scope = preview
        ? previewScope(preview, t, nodes.find(entry => entry.id === preview.triggerNodeId)?.data.type as NodeType | undefined)
        : null;
    const broken = count?.kind === 'failed' || (count?.kind === 'count' && count.failed);
    const {lists, note} = preview && node && type
        ? stepLists({preview, nodeId: node.id, type, config: node.data.config, label, t, collator})
        : {lists: [], note: null};

    const allRows = lists.flatMap(list => list.rows);
    const columns: PeopleColumns = {
        stay: allRows.some(row => row.arrival),
        room: allRows.some(row => row.room),
        language: allRows.some(row => row.language),
    };

    const people = allRows.length;
    const query = search.trim().toLocaleLowerCase();
    const anyShown = allRows.some(row => matchesSearch(row, query));
    const limited = preview?.fanOuts.find(fanOut => fanOut.nodeId === nodeId && fanOut.limited && fanOut.total != null);

    return (
        <Dialog
            open={!!node}
            onClose={onClose}
            maxWidth={'md'}
            fullWidth
            PaperProps={{sx: {height: people > SEARCH_FROM ? 'min(800px, 90vh)' : undefined, maxHeight: '90vh'}}}
        >
            <DialogTitle sx={{p: 2.5, pb: 2}}>
                <Stack direction={'row'} alignItems={'center'} gap={1.5}>
                    {type && (
                        <Box sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: 1.5,
                            color,
                            backgroundColor: alpha(color, 0.12),
                            '& .MuiSvgIcon-root': {fontSize: 22},
                        }}>
                            {getNodeDisplay(type).icon}
                        </Box>
                    )}
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography variant={'h6'} fontWeight={600} noWrap sx={{lineHeight: 1.3}}>{label}</Typography>
                        {meaning && (
                            <Typography variant={'body1'} color={broken ? 'error.main' : 'text.secondary'} noWrap>
                                <BoldNumbers text={scope ? `${meaning} · ${scope}` : meaning}/>
                            </Typography>
                        )}
                    </Box>
                    <Tooltip title={t('Close')}>
                        <IconButton aria-label={t('Close')} onClick={onClose} sx={{flexShrink: 0, alignSelf: 'flex-start'}}>
                            <CloseIcon/>
                        </IconButton>
                    </Tooltip>
                </Stack>

                {people > SEARCH_FROM && (
                    <TextField
                        fullWidth
                        size={'small'}
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder={t('Search')}
                        sx={{mt: 2}}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position={'start'}>
                                    <SearchIcon fontSize={'small'}/>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </DialogTitle>

            <DialogContent sx={{px: 2.5, pb: 2.5}}>
                {!preview ? (
                    <Stack alignItems={'center'} justifyContent={'center'} sx={{py: 6}}>
                        <CircularProgress size={28}/>
                    </Stack>
                ) : (
                    <Stack gap={3}>
                        {note && <Alert severity={'info'}>{note}</Alert>}
                        {preview.timedOut && (
                            <Alert severity={'warning'}>
                                {t('Preview timed out', {checked: numbers.format(preview.members.length)})}
                            </Alert>
                        )}
                        {limited && !preview.timedOut && (
                            <Alert severity={'info'}>
                                {t('Preview limited', {
                                    evaluated: numbers.format(limited.evaluated),
                                    total: numbers.format(limited.total!)
                                })}
                            </Alert>
                        )}

                        {lists.map(list => (
                            <StepPeopleTable
                                key={`${nodeId}-${list.title}`}
                                list={list}
                                columns={columns}
                                query={query}
                            />
                        ))}

                        {!lists.length && !note && (
                            <Typography variant={'body1'} color={'text.secondary'} sx={{py: 4, textAlign: 'center'}}>
                                {t('Nobody gets this far right now')}
                            </Typography>
                        )}
                        {!!lists.length && !anyShown && (
                            <Typography variant={'body1'} color={'text.secondary'} sx={{py: 4, textAlign: 'center'}}>
                                {t('No results found')}
                            </Typography>
                        )}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default StepPreviewDialog;
